/*
 * Copyright (C) 2023 Nick Robinson
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

package uk.co.nickthecoder.glok.text

import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import uk.co.nickthecoder.glok.backend.Texture
import uk.co.nickthecoder.glok.backend.webgl.LoadedWebGLTexture
import uk.co.nickthecoder.glok.util.max
import uk.co.nickthecoder.glok.util.min
import kotlin.math.ceil

actual fun createBitmapFont(
    identifier: FontIdentifier,
    block: (BitmapFontBuilder.() -> Unit)?
): BitmapFont {

    val builder = BitmapFontBuilderFromCanvas(identifier)
    if (block != null) {
        builder.block()
    }
    return builder.build()
}


/**
 * The internal workhorse behind [createBitmapFont].
 *
 */
class BitmapFontBuilderFromCanvas(

    val identifier: FontIdentifier

) : BitmapFontBuilder {

    private var xPadding: Int = 2
    private var yPadding: Int = 2
    private var maxTextureWidth: Int = 800

    private val requiredChars = mutableSetOf<Char>()

    override fun includeGlyphs(chars: Set<Char>) {
        requiredChars.addAll(chars)
    }

    override fun padding(x: Int, y: Int) {
        xPadding = x
        yPadding = y
    }

    override fun maxTextureWidth(value: Int) {
        maxTextureWidth = value
    }

    fun build(): BitmapFont {

        //log.info("Creating BitmapFont")
        val styleString = when (identifier.style) {
            FontStyle.PLAIN -> ""
            FontStyle.BOLD -> "bold"
            FontStyle.ITALIC -> "italic"
            FontStyle.BOLD_ITALIC -> "bold italic"
        }

        val fontString = "$styleString ${identifier.size}px ${identifier.family}"

        val glyphData = mutableMapOf<Char, BitmapGlyphData?>()

        val canvas = window.document.getElementById("fontCanvas") as HTMLCanvasElement
        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D

        // Guess an appropriate size for the canvas to draw all the glyphs.
        // If the guess height is too small, the canvas will be resized later.
        val approxGlyphCount = 100
        canvas.width = min(maxTextureWidth, ceil(identifier.size * 20f).toInt())
        canvas.height = max(
            ceil(identifier.size * 2f),
            ceil(identifier.size * identifier.size * approxGlyphCount / canvas.width)
        ).toInt()

        //log.info("Canvas size : ${canvas.width}x${canvas.height}")
        ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())

        // Use the appropriate font, and draw in white.
        ctx.font = fontString
        ctx.fillStyle = "white"

        val defaultMetrics = ctx.measureText("_")
        val defaultAscent = defaultMetrics.fontBoundingBoxAscent.toInt()
        val defaultDescent = defaultMetrics.fontBoundingBoxDescent.toInt()
        val fontHeight = defaultAscent + defaultDescent

        var x = xPadding
        var y = defaultAscent + yPadding

        if (glyphData.isEmpty()) {
            includeGlyphs(BitmapFont.requiredGlyphs)
        }

        for (c in requiredChars) {
            val cMetrics = ctx.measureText(c.toString())
            val glyphWidth = (ceil(- cMetrics.actualBoundingBoxLeft) + ceil(cMetrics.actualBoundingBoxRight)).toInt()

            // Have we reached the end of the line of glyphs?
            if (x + glyphWidth >= canvas.width - xPadding) {
                // Move onto the next line
                y += fontHeight + yPadding
                x = xPadding

                // Have we run out of space for the next line of glyphs?
                if (y + defaultDescent + yPadding > canvas.height) {
                    // Grow the canvas in Y by a factor of 2.
                    //console.log("Redraw with height " + canvas.height + " at char " + c)

                    // Resizing the canvas will clear it, so save its contents, resize the canvas and then draw the saved contents.
                    val oldImage = ctx.getImageData(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                    canvas.height *= 2
                    // If we want to 100% make sure the canvas is cleared...
                    // ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                    ctx.putImageData(oldImage, 0.0, 0.0)

                    // Resizing a canvas destroys the state, so restate the font and fill style.
                    ctx.font = fontString
                    ctx.fillStyle = "white"
                }
            }

            // Was this glyph found (with a special case for the SPACE character).
            if (glyphWidth <= 0) {
                if (c != ' ') {
                    glyphData[c] = null
                    continue
                }
            } else {
                x += ceil(- cMetrics.actualBoundingBoxLeft).toInt()
                ctx.fillText(c.toString(), x.toDouble(), y.toDouble())
            }

            val tripleMetrics = ctx.measureText("_${c}_")
            val advance = ceil(- tripleMetrics.actualBoundingBoxLeft) + ceil(tripleMetrics.actualBoundingBoxRight) -
                2 * (ceil(- defaultMetrics.actualBoundingBoxLeft) + ceil(defaultMetrics.actualBoundingBoxRight))

            // Yes the glyph was found. Let's store it in the map.
            glyphData[c] = BitmapGlyphData(
                x = x.toFloat(),
                y = y.toFloat(),
                boundsTop = ceil(cMetrics.actualBoundingBoxAscent).toFloat(),
                boundsRight = ceil(cMetrics.actualBoundingBoxRight).toFloat(),
                boundsBottom = ceil(cMetrics.actualBoundingBoxDescent).toFloat(),
                boundsLeft = ceil(- cMetrics.actualBoundingBoxLeft).toFloat(),
                advance = advance.toFloat(),
            )

            x += ceil(cMetrics.actualBoundingBoxRight).toInt() + xPadding
        }

        val imageData = ctx.getImageData(0.0, 0.0, canvas.width.toDouble(), y + yPadding + defaultDescent.toDouble())
        val texture: Texture = LoadedWebGLTexture(fontString, imageData.width, imageData.height, imageData)

        val font = BitmapFont(
            identifier,
            texture,
            glyphData,
            height = fontHeight.toFloat(),
            ascent = defaultAscent.toFloat(),
            descent = defaultDescent.toFloat(),
            top = defaultAscent.toFloat(),
            bottom = defaultDescent.toFloat(),
            italicInverseSlope = 0f // TODO ???
        )

        // Visual test that the glyph data is OK. Red bounding boxes should appear around the glyphs in the word hello.
        /*
        ctx.strokeStyle = "red"
        for (c in "Hello") {
            val data = glyphData[c] ?: continue
            ctx.strokeRect(
                data.x.toDouble() - data.boundsLeft, (data.y - font.ascent).toDouble(),
                data.width.toDouble(), font.height.toDouble()
            )
        }
        */

        return font
    }

}
